import { z } from 'zod/v4';

export type InactivityMode = 'disabled' | 'sign_out' | 'pin_lock';

export interface SessionInactivityPolicy {
  mode: InactivityMode;
  timeoutMinutes: number;
  pinConfigured: boolean;
}

export type SessionInactivityOutcome = 'none' | 'sign_out' | 'pin_lock';

export const DEFAULT_SESSION_INACTIVITY_POLICY: Readonly<SessionInactivityPolicy> =
  Object.freeze({
    // Inactivity protection is an explicit company choice. Authentication
    // expiry, revocation and manual sign-out remain independent of it.
    mode: 'disabled',
    timeoutMinutes: 50,
    pinConfigured: false,
  });

export const SESSION_INACTIVITY_MODES = [
  'disabled',
  'sign_out',
  'pin_lock',
] as const satisfies readonly InactivityMode[];
export const MIN_SESSION_INACTIVITY_TIMEOUT_MINUTES = 5;
export const MAX_SESSION_INACTIVITY_TIMEOUT_MINUTES = 240;
export const SESSION_UNLOCK_PIN_LENGTH = 6;
export const SESSION_INACTIVITY_WARNING_MS = 60_000;

/** Strict mutation boundary shared by forms and data-service adapters. */
export const sessionInactivityPolicyUpdateSchema = z
  .object({
    mode: z.enum(SESSION_INACTIVITY_MODES),
    timeoutMinutes: z
      .number()
      .int()
      .min(MIN_SESSION_INACTIVITY_TIMEOUT_MINUTES)
      .max(MAX_SESSION_INACTIVITY_TIMEOUT_MINUTES),
  })
  .strict();

export type SessionInactivityPolicyUpdateInput = z.infer<
  typeof sessionInactivityPolicyUpdateSchema
>;

/** ASCII digits only, matching the Postgres RPC contract exactly. */
export const sessionUnlockPinSchema = z
  .string()
  .length(SESSION_UNLOCK_PIN_LENGTH)
  .regex(/^[0-9]+$/);

/** `null` is the explicit remove-PIN operation. */
export const sessionUnlockPinMutationSchema = sessionUnlockPinSchema.nullable();

function isRecord(value: unknown): value is Record<string, unknown> {
  return typeof value === 'object' && value !== null && !Array.isArray(value);
}

function isInactivityMode(value: unknown): value is InactivityMode {
  return (
    typeof value === 'string' &&
    (SESSION_INACTIVITY_MODES as readonly string[]).includes(value)
  );
}

function normalizeTimeoutMinutes(value: unknown): number {
  return typeof value === 'number' &&
    Number.isInteger(value) &&
    value >= MIN_SESSION_INACTIVITY_TIMEOUT_MINUTES &&
    value <= MAX_SESSION_INACTIVITY_TIMEOUT_MINUTES
    ? value
    : DEFAULT_SESSION_INACTIVITY_POLICY.timeoutMinutes;
}

/**
 * Normalise une valeur issue d'une frontière non typée (RPC, cache local,
 * ancienne version du client) en appliquant la politique par défaut.
 */
export function normalizeSessionInactivityPolicy(
  raw: unknown,
): SessionInactivityPolicy {
  if (!isRecord(raw)) return { ...DEFAULT_SESSION_INACTIVITY_POLICY };

  const mode = raw.mode ?? raw.inactivity_mode;
  const timeoutMinutes = raw.timeoutMinutes ?? raw.timeout_minutes;
  const pinConfigured = raw.pinConfigured ?? raw.pin_configured;

  return {
    mode: isInactivityMode(mode)
      ? mode
      : DEFAULT_SESSION_INACTIVITY_POLICY.mode,
    timeoutMinutes: normalizeTimeoutMinutes(timeoutMinutes),
    pinConfigured:
      typeof pinConfigured === 'boolean'
        ? pinConfigured
        : DEFAULT_SESSION_INACTIVITY_POLICY.pinConfigured,
  };
}

/** Boundary inclusive: exactly N inactive minutes means the policy expired. */
export function isSessionInactivityExpired(
  lastActivityAt: number,
  now: number,
  policy: SessionInactivityPolicy,
): boolean {
  const normalized = normalizeSessionInactivityPolicy(policy);
  if (normalized.mode === 'disabled') return false;
  if (
    !Number.isFinite(lastActivityAt) ||
    !Number.isFinite(now) ||
    now < lastActivityAt
  ) {
    return false;
  }

  return now - lastActivityAt >= normalized.timeoutMinutes * 60_000;
}

/**
 * A PIN policy without a PIN must fail closed to a full sign-out. This avoids
 * presenting a lock screen that has no credential capable of unlocking it.
 */
export function resolveSessionInactivityOutcome(
  policy: SessionInactivityPolicy,
): SessionInactivityOutcome {
  const normalized = normalizeSessionInactivityPolicy(policy);
  if (normalized.mode === 'disabled') return 'none';
  if (normalized.mode === 'pin_lock' && normalized.pinConfigured)
    return 'pin_lock';
  return 'sign_out';
}
